/**
 * Formattazione dei numeri, in un posto solo.
 *
 * ⚠️ Perché esiste: `(7072).toLocaleString("it-IT")` restituisce **«7072»**, non
 * «7.072». L'ICU italiano ha `minimumGroupingDigits = 2`, quindi i numeri di
 * quattro cifre non vengono raggruppati e in tabella finiscono accanto a «12.048».
 * È il difetto del raggruppamento numerico ricomparso tre volte in M2 — la causa
 * non era una `toLocaleString` dimenticata, era `toLocaleString` stessa.
 *
 * La cura sarebbe `useGrouping: "always"`, che però non è nei tipi della lib TS
 * del progetto: qui il raggruppamento è fatto a mano, e così non dipende né dal
 * locale né dalla versione dell'ICU.
 */

/** Migliaia col punto e decimali con la virgola, sempre. */
function formatta(n: number, cifre: number): string {
  const negativo = n < 0;
  const fisso = Math.abs(n).toFixed(cifre);
  const [interi, decimali] = fisso.split(".");
  const raggruppati = interi.replace(/\B(?=(\d{3})+(?!\d))/g, ".");
  const segno = negativo ? "−" : "";
  return decimali ? `${segno}${raggruppati},${decimali}` : `${segno}${raggruppati}`;
}

/** Numero intero, raggruppato. `null`/`NaN` → «—». */
export function num(valore: string | number | null | undefined): string {
  const n = normalizza(valore);
  return n === null ? "—" : formatta(n, 0);
}

/** Numero con decimali fissi, raggruppato. */
export function dec(valore: string | number | null | undefined, cifre = 1): string {
  const n = normalizza(valore);
  return n === null ? "—" : formatta(n, cifre);
}

/** Chili: sopra la tonnellata si legge in tonnellate, che è la scala del piano. */
export function kg(valore: string | number | null | undefined): string {
  const n = normalizza(valore);
  if (n === null) return "—";
  if (Math.abs(n) >= 10_000) return `${formatta(n / 1000, 1)} t`;
  return `${formatta(n, 0)} kg`;
}

/** Ore di macchina, sempre con un decimale: «0 ore» e «0,4 ore» non sono uguali. */
export function ore(valore: string | number | null | undefined): string {
  const n = normalizza(valore);
  return n === null ? "—" : `${formatta(n, 1)} h`;
}

/** Minuti → ore, per le durate del piano che arrivano in minuti. */
export function minutiInOre(minuti: string | number | null | undefined): string {
  const n = normalizza(minuti);
  return n === null ? "—" : ore(n / 60);
}

export function pct(valore: number | null | undefined, cifre = 0): string {
  const n = normalizza(valore);
  return n === null ? "—" : `${formatta(n, cifre)}%`;
}

/** Segno esplicito: su un saldo «−38.822» e «38.822» dicono cose opposte. */
export function conSegno(valore: string | number | null | undefined): string {
  const n = normalizza(valore);
  if (n === null) return "—";
  return `${n < 0 ? "−" : "+"}${formatta(Math.abs(n), 0)}`;
}

function normalizza(valore: string | number | null | undefined): number | null {
  if (valore === null || valore === undefined) return null;
  const n = typeof valore === "string" ? parseFloat(valore) : valore;
  return Number.isNaN(n) ? null : n;
}
